import { mdiAlert } from '@mdi/js';
import Icon from '@mdi/react';
import axios from 'axios';
import LogRocket from 'logrocket';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getLoggedInUser } from 'src/actions';
import { useAuth0 } from 'src/applicationproviders';
import Empty from 'src/components/baseComponents/Empty';
import Loading from 'src/components/baseComponents/Loading';
import { useCurrentLoanOfficer } from 'src/hooks';
import { apiAxios } from 'src/util';
import { Link, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';

export const AuthenticationGate = ({ children }) => {
  const dispatch = useDispatch();
  const { ready, authenticated, user, getToken, login } = useAuth0();
  const loanOfficer = useCurrentLoanOfficer();
  const location = useLocation();
  const [apisReady, setApisReady] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const justHandledCallbackRef = useRef(false);
  const redirectTimeoutRef = useRef(null);

  // Track when we're on or just left the callback page
  useEffect(() => {
    if (location.pathname?.includes('/callback')) {
      justHandledCallbackRef.current = true;
    } else if (justHandledCallbackRef.current) {
      // Give Auth0 a moment to update authenticated state after leaving callback
      const timer = setTimeout(() => {
        justHandledCallbackRef.current = false;
      }, 3000); // Increased to 3 seconds
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      // Don't try to authenticate if we're on callback page or just handled it
      if (
        ready &&
        !authenticated &&
        !isAuthenticating &&
        !justHandledCallbackRef.current &&
        !location.pathname?.includes('/callback')
      ) {
        setIsAuthenticating(true);
        try {
          await getToken(true);
        } catch (e) {
          // Ignore expected authentication flow errors
          if (isMounted) {
            setError(e);
          }
        } finally {
          if (isMounted) {
            setIsAuthenticating(false);
          }
        }
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, ready, location.pathname]);

  useEffect(() => {
    const setAuthToken = async (config) => {
      if (!authenticated) return config;

      const token = await getToken(false);
      config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    };
    const apiInterceptorId = apiAxios.interceptors.request.use(setAuthToken);
    const apiInterceptorId2 = axios.interceptors.request.use(setAuthToken);
    setApisReady(true);

    return () => {
      apiAxios.interceptors.request.eject(apiInterceptorId);
      axios.interceptors.request.eject(apiInterceptorId2);
    };
  }, [getToken, authenticated]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (authenticated && apisReady) {
        try {
          await dispatch(
            getLoggedInUser({
              licenses: true,
              personas: true,
            }),
          );
        } catch (e) {
          if (isMounted) {
            setError(e);
          }
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [authenticated, dispatch, apisReady]);

  useEffect(() => {
    if (user?.sub) {
      LogRocket.identify(user.sub, {
        name: user.name,
        email: user.email,
      });
      if (!user.sub.startsWith('waad')) {
        setError({ message: 'Not logged in as loan officer' });
      }
    }
  }, [user?.sub, authenticated, user?.name, user?.email]);

  // If loading hasn't resolved within 25 seconds, show a message for 2s then restart the auth flow.
  useEffect(() => {
    if (authenticated && apisReady && Object.keys(loanOfficer).length) return;

    const timeout = setTimeout(() => {
      setIsRedirecting(true);
      redirectTimeoutRef.current = setTimeout(() => {
        login({
          appState: { targetUrl: `${location.pathname}${location.search}` },
        });
      }, 2000);
    }, 25_000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(redirectTimeoutRef.current);
    };
  }, [
    authenticated,
    apisReady,
    loanOfficer,
    login,
    location.pathname,
    location.search,
  ]);

  const loadingMessage = useMemo(() => {
    if (isRedirecting) return 'Taking too long, redirecting to login...';
    if (!authenticated || !apisReady) return 'Authenticating...';
    if (!Object.keys(loanOfficer).length) return 'Loading user information...';
    return null;
  }, [apisReady, authenticated, loanOfficer, isRedirecting]);

  if (error) {
    return (
      <Empty
        icon={<Icon path={mdiAlert} />}
        text={error.response?.data?.messages?.[0] ?? error.message}
      >
        <Typography variant='body1'>
          Please <Link to='/logout'>try logging in again.</Link>
        </Typography>
      </Empty>
    );
  }
  if (authenticated && apisReady && Object.keys(loanOfficer).length)
    return children;
  return <Loading label={loadingMessage} />;
};
